/**
 * AgentusHojeWidget — v0.99.44 · Fev/2026 (passo 6)
 *
 * Painel operacional baseado EXCLUSIVAMENTE em eventos reais do
 * Employee Coordinator. Fonte: GET /api/coordinator/digest/today.
 *
 * Mostra:
 *   • totals: tentativas / autorizadas / bloqueadas / adiadas
 *   • por funcionário digital: 3 contadores por employee
 *   • "N contatos evitados hoje" — demonstra inteligência operacional
 *     ("deixou de incomodar X clientes" é a venda do briefing)
 *
 * Sem invenção de números. Se o Coordinator ainda não logou nada,
 * mostra estado zero elegante.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Prohibit, Clock, Robot } from "@phosphor-icons/react";

const EMPLOYEE_LABELS = {
  appointment_reminder_1h: "Lembrete 1h",
  appointment_reminder_24h: "Lembrete 24h",
  urgent_pastoral: "Urgência Pastoral",
  post_service: "Pós-serviço",
  clinical_followup: "Follow-up Clínico",
  budget_followup: "Orçamentista",
  no_reply_followup: "Sem Resposta",
  birthday: "Aniversário",
  asset_birthday: "Aniversário do Ativo",
  capture_nudge: "Capture Nudge",
  upsell_opportunity: "Oportunidade",
  recuperador: "Recuperador",
  campaign_scheduled: "Campanha",
  campaign_recurring: "Campanha Recorrente",
  // v0.99.46 · Fev/2026 — Mandato & Governo
  mandate_topic_update: "Atualização de Tema",
  mandate_demand_update: "Atualização de Demanda",
  // v0.99.47 · Fev/2026 — Agenda + Prestação de Contas
  mandate_agenda_notice: "Agenda Pública",
  mandate_accountability_digest: "Prestação de Contas",
};

function Stat({ icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div
      data-testid={testid}
      className={`flex-1 rounded-lg border px-3 py-2.5 ${tones[tone]}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function AgentusHojeWidget() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/coordinator/digest/today");
        if (on) setDigest(data);
      } catch (e) {
        if (on) setErr(e?.response?.data?.detail || "falha ao carregar");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="agentus-hoje-loading"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        <div className="h-20 animate-pulse rounded-md bg-slate-100" />
      </div>
    );
  }
  if (err || !digest) {
    return (
      <div
        data-testid="agentus-hoje-error"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm text-sm text-slate-500"
      >
        Agentus Hoje — não foi possível carregar ({String(err)}).
      </div>
    );
  }

  const totals = digest.totals || {};
  const byEmp = digest.by_employee || {};
  const empRows = Object.entries(byEmp)
    .map(([k, v]) => ({ key: k, ...v }))
    .filter((r) => (r.allowed || 0) + (r.blocked || 0) + (r.deferred || 0) > 0)
    .sort((a, b) => (b.allowed || 0) - (a.allowed || 0));

  const prevented = (totals.blocked || 0) + (totals.deferred || 0);

  return (
    <div
      data-testid="agentus-hoje-widget"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Robot size={18} weight="bold" className="text-slate-700" />
            <h3 className="font-heading text-base font-semibold tracking-tight text-slate-900">
              Agentus hoje
            </h3>
          </div>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Decisões reais do dia · {digest.day || "hoje"} ·{" "}
            {digest.timezone || "America/Sao_Paulo"}
          </p>
        </div>
        {prevented > 0 && (
          <div
            data-testid="agentus-hoje-prevented"
            className="rounded-full bg-slate-900 text-white px-3 py-1 text-[12px] font-medium"
            title="Contatos evitados para não sobrecarregar clientes."
          >
            {prevented} {prevented === 1 ? "contato evitado" : "contatos evitados"}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Stat
          icon={<CheckCircle size={13} weight="bold" />}
          label="Ações executadas"
          value={totals.allowed || 0}
          tone="emerald"
          testid="agentus-hoje-allowed"
        />
        <Stat
          icon={<Prohibit size={13} weight="bold" />}
          label="Bloqueadas"
          value={totals.blocked || 0}
          tone="rose"
          testid="agentus-hoje-blocked"
        />
        <Stat
          icon={<Clock size={13} weight="bold" />}
          label="Adiadas"
          value={totals.deferred || 0}
          tone="amber"
          testid="agentus-hoje-deferred"
        />
      </div>

      {empRows.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">
            Por funcionário digital
          </div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
            {empRows.map((row) => (
              <div
                key={row.key}
                data-testid={`agentus-hoje-emp-${row.key}`}
                className="flex items-center justify-between px-3 py-2 text-[13px]"
              >
                <span className="text-slate-700 font-medium">
                  {EMPLOYEE_LABELS[row.key] || row.key}
                </span>
                <span className="flex items-center gap-3 text-[12px] text-slate-500 tabular-nums">
                  <span className="text-emerald-600">✓ {row.allowed || 0}</span>
                  {(row.blocked || 0) > 0 && (
                    <span className="text-rose-600">✕ {row.blocked}</span>
                  )}
                  {(row.deferred || 0) > 0 && (
                    <span className="text-amber-600">⌛ {row.deferred}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {empRows.length === 0 && (totals.attempted || 0) === 0 && (
        <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 text-[13px] text-slate-500">
          Nenhuma decisão do Agentus até agora hoje. Conforme crons rodam
          e eventos acontecem, as ações aparecem aqui em tempo real.
        </div>
      )}
    </div>
  );
}
